import { useCallback, useMemo, useState } from 'react';

import { DownOutlined, DownloadOutlined } from '@ant-design/icons';
import { Button, Dropdown, Grid, message, Space } from 'antd';
import type { ButtonProps, MenuProps } from 'antd';

import type { ExportFormat } from './index.ts';
import { downloadExport } from './utils.ts';
import type { ExportFilters } from './utils.ts';
import { useCanExportData } from './useCanExportData.ts';

export interface ExportDataButtonProps {
  urlName: string;
  urlArgs?: Array<string | number | boolean>;
  filters?: ExportFilters;
  defaultFileName?: string;
  primaryFormat?: ExportFormat;
  label?: string;
  disabled?: boolean;
  type?: ButtonProps['type'];
  size?: ButtonProps['size'];
  omitKeys?: readonly string[];
}

export const ExportDataButton = ({
  urlName,
  urlArgs,
  filters,
  defaultFileName,
  label,
  disabled,
  type = 'default',
  size,
  omitKeys,
}: ExportDataButtonProps) => {
  const canExport = useCanExportData();
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;
  const [loadingFormat, setLoadingFormat] = useState<ExportFormat | null>(null);

  const runExport = useCallback(
    async (format: ExportFormat) => {
      setLoadingFormat(format);
      try {
        await downloadExport({
          urlName,
          urlArgs,
          filters,
          format,
          defaultFileName,
          omitKeys,
        });
        message.success('Exportación lista.');
      } catch (error) {
        message.error(error instanceof Error ? error.message : 'No se pudo exportar el archivo.');
      } finally {
        setLoadingFormat(null);
      }
    },
    [defaultFileName, filters, omitKeys, urlArgs, urlName],
  );

  const menu = useMemo<MenuProps>(
    () => ({
      items: [
        {
          key: 'xlsx',
          label: 'Exportar como XLSX',
          onClick: () => {
            void runExport('xlsx');
          },
        },
        {
          key: 'csv',
          label: 'Exportar como CSV',
          onClick: () => {
            void runExport('csv');
          },
        },
      ],
    }),
    [runExport],
  );

  if (!canExport) {
    return null;
  }

  return (
    <Dropdown
      menu={menu}
      disabled={disabled || Boolean(loadingFormat)}
      trigger={['click']}
    >
      <Button
        icon={<DownloadOutlined />}
        loading={Boolean(loadingFormat)}
        disabled={disabled || Boolean(loadingFormat)}
        type={type}
        size={size}
      >
        {!isMobile && (
          <Space size={4}>
            <span>{label ?? 'Exportar'}</span>
            <DownOutlined style={{ fontSize: 10 }} />
          </Space>
        )}
      </Button>
    </Dropdown>
  );
};
